import React, { useState, useEffect } from "react";
import api from "./api";

const REVIEW_TYPE_LABELS = {
  initial:        { label: "Initial Consultation", color: "#16a34a", bg: "#dcfce7", icon: "🌱" },
  review:         { label: "Review",               color: "#2563eb", bg: "#dbeafe", icon: "✏️" },
  plan_submitted: { label: "Plan Submitted",       color: "#7c3aed", bg: "#ede9fe", icon: "📋" },
  reset:          { label: "Reset",                color: "#dc2626", bg: "#fee2e2", icon: "↩" },
};

function fmt(date) {
  if (!date) return "—";
  const d = new Date(date);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
    " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function DiffBadge({ items, color, icon }) {
  if (!items?.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 4, margin: "3px 0" }}>
      <span style={{ fontSize: 13, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 12, color }}>{items.join(", ")}</span>
    </div>
  );
}

function ReviewCard({ review, idx, isLatest }) {
  const [expanded, setExpanded] = useState(isLatest);
  const meta = REVIEW_TYPE_LABELS[review.reviewType] || REVIEW_TYPE_LABELS.review;
  const ch   = review.changes || {};
  const hasChanges = [
    ch.sequencesAdded, ch.sequencesRemoved, ch.sequencesModified,
    ch.recommendationsAdded, ch.recommendationsRemoved, ch.bodyPartsChanged,
  ].some(a => a?.length > 0);

  return (
    <div style={{ borderRadius: 10, border: `1.5px solid ${isLatest ? meta.color : "#e5e7eb"}`,
      marginBottom: 10, overflow: "hidden", background: isLatest ? meta.bg : "#fafafa" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", cursor: "pointer", background: isLatest ? meta.bg : "#f3f4f6" }}
        onClick={() => setExpanded(e => !e)}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18 }}>{meta.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: meta.color }}>{meta.label}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>
              {fmt(review.date)}
              {review.reviewedBy && <span> · By: <strong>{review.reviewedBy}</strong></span>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isLatest && <span style={{ fontSize: 10, background: meta.color, color: "#fff",
            borderRadius: 99, padding: "2px 8px", fontWeight: 700 }}>LATEST</span>}
          {!hasChanges && review.reviewType !== 'initial' && (
            <span style={{ fontSize: 11, color: "#aaa" }}>No changes</span>
          )}
          <span style={{ fontSize: 14, color: "#888" }}>{expanded ? "▴" : "▾"}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "10px 14px", borderTop: "1px solid #e5e7eb" }}>

          {/* Diff section */}
          {hasChanges && (
            <div style={{ marginBottom: 10, padding: "8px 10px", background: "#fff",
              borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4,
                textTransform: "uppercase", letterSpacing: "0.05em" }}>Changes</div>
              <DiffBadge items={ch.sequencesAdded}          color="#16a34a" icon="➕" />
              <DiffBadge items={ch.sequencesRemoved}         color="#dc2626" icon="➖" />
              <DiffBadge items={ch.sequencesModified}        color="#d97706" icon="✏️" />
              <DiffBadge items={ch.recommendationsAdded}     color="#16a34a" icon="➕" />
              <DiffBadge items={ch.recommendationsRemoved}   color="#dc2626" icon="➖" />
              <DiffBadge items={ch.bodyPartsChanged}         color="#7c3aed" icon="📊" />
            </div>
          )}

          {/* Sessions */}
          {(review.mythsessions != null || review.maintsessions != null) && (
            <div style={{ marginBottom: 8 }}>
              <div style={sStyles.snapLabel}>Sessions</div>
              <div style={{ fontSize: 12, color: "#555" }}>
                Treatment: <strong>{review.mythsessions ?? "—"}</strong>
                {" · "}Maintenance: <strong>{review.maintsessions ?? "—"}</strong>
                {" · "}Preventive: <strong>{review.preventivesessions ?? "—"}</strong>
              </div>
            </div>
          )}

          {/* Body grades */}
          {review.bodyGrades && Object.keys(review.bodyGrades).length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={sStyles.snapLabel}>Pain Grades</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {Object.entries(review.bodyGrades).map(([part, grade]) => (
                  <span key={part} style={sStyles.grade}>
                    {part} <strong>{grade}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Exercises */}
          {review.sequences?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={sStyles.snapLabel}>Exercises ({review.sequences.length})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {review.sequences.map((s, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#374151", display: "flex", gap: 8 }}>
                    <span style={{ color: "#6b7280" }}>•</span>
                    <span>{s.name}</span>
                    {s.duration   && <span style={sStyles.meta}>{s.duration}s</span>}
                    {s.repetition && <span style={sStyles.meta}>×{s.repetition}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {review.recommendations?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={sStyles.snapLabel}>Recommendations ({review.recommendations.length})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {review.recommendations.map((r, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#374151", display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ color: "#6b7280" }}>•</span>
                    <span>{r.text}</span>
                    {r.qty  && <span style={sStyles.meta}>Qty: {r.qty}</span>}
                    {r.not  && <span style={sStyles.meta}>{r.not}×/day</span>}
                    {r.nod  && <span style={sStyles.meta}>{r.nod} days</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Patient comments (per body part) */}
          {review.partComments && Object.keys(review.partComments).length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={sStyles.snapLabel}>Patient Problems</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {Object.entries(review.partComments).map(([part, comment]) =>
                  comment ? (
                    <div key={part} style={{ fontSize: 12, color: "#374151" }}>
                      <span style={sStyles.grade}>{part}</span>{" "}
                      <span style={{ fontStyle: "italic" }}>{comment}</span>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          )}

          {/* General comments */}
          {review.generalComments && (
            <div style={{ marginBottom: 8 }}>
              <div style={sStyles.snapLabel}>General Comments</div>
              <div style={{ fontSize: 12, color: "#374151", fontStyle: "italic" }}>{review.generalComments}</div>
            </div>
          )}

          {/* Doctor comments */}
          {review.drcomments && (
            <div>
              <div style={sStyles.snapLabel}>Doctor Comments</div>
              <div style={{ fontSize: 12, color: "#374151", fontStyle: "italic" }}>{review.drcomments}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BillCard({ bill, idx }) {
  const [expanded, setExpanded] = useState(idx === 0);
  const isReRoot = (bill.billorg || '').toLowerCase().includes('reroot');
  const dateStr = bill.invoicedate ? new Date(bill.invoicedate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const services = bill.services || [];

  return (
    <div style={{
      borderRadius: 10, marginBottom: 8, overflow: "hidden",
      border: isReRoot ? "2px solid #a855f7" : "1.5px solid #e5e7eb",
      background: isReRoot ? "#faf5ff" : "#fafafa",
    }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", cursor: "pointer",
          background: isReRoot ? "#f3e8ff" : "#f3f4f6" }}
        onClick={() => setExpanded(e => !e)}
      >
        {isReRoot && <span style={{ fontSize: 14 }}>💜</span>}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: isReRoot ? "#7c3aed" : "#374151" }}>
            {bill.invoicenumber || `Bill #${idx + 1}`}
            {isReRoot && <span style={{ marginLeft: 6, fontSize: 10, background: "#a855f7", color: "#fff", borderRadius: 99, padding: "1px 7px", fontWeight: 700 }}>ReRoot</span>}
          </div>
          <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>
            {dateStr} · {services.length} service(s) · ₹{(bill.total || 0).toLocaleString("en-IN")}
            {bill.balance > 0 && <span style={{ color: "#dc2626", marginLeft: 6 }}>(Balance: ₹{bill.balance})</span>}
          </div>
        </div>
        <span style={{ fontSize: 12, color: "#888" }}>{expanded ? "▴" : "▾"}</span>
      </div>

      {expanded && services.length > 0 && (
        <div style={{ padding: "8px 14px 12px" }}>
          {services.map((s, si) => {
            const sIsReRoot = (s.org || '').toLowerCase().includes('reroot');
            return (
              <div key={si} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", marginBottom: 4,
                borderRadius: 7, background: sIsReRoot ? "#ede9fe" : "#fff",
                border: sIsReRoot ? "1px solid #c4b5fd" : "1px solid #f3f4f6",
              }}>
                {sIsReRoot && <span style={{ fontSize: 12 }}>💜</span>}
                <span style={{ fontSize: 13, fontWeight: 600, color: sIsReRoot ? "#6d28d9" : "#374151", flex: 1 }}>{s.name}</span>
                <span style={{ fontSize: 12, color: "#6b7280" }}>Qty: {s.qty || 1}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: sIsReRoot ? "#7c3aed" : "#374151" }}>₹{(s.amount || 0).toLocaleString("en-IN")}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ConsultationHistoryModal({ patientCard, patientName, onClose }) {
  const [history, setHistory]   = useState([]);
  const [bills,   setBills]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [activeTab, setActiveTab] = useState("history");

  useEffect(() => {
    Promise.all([
      api.get(`${process.env.REACT_APP_URL}/patients/consultation/${patientCard}/review-history`),
      api.get(`${process.env.REACT_APP_URL}/patients/profiles/${patientCard}`),
    ])
      .then(([histRes, profileRes]) => {
        setHistory(histRes.data || []);
        const profileData = profileRes.data;
        setBills((profileData?.bills || profileData?._doc?.bills || []).sort((a, b) => new Date(b.invoicedate) - new Date(a.invoicedate)));
      })
      .catch(() => setError("Could not load history."))
      .finally(() => setLoading(false));
  }, [patientCard]);

  const tabBtn = (id, label) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: "6px 16px", border: "none", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
        background: activeTab === id ? (id === "bills" ? "#7c3aed" : "#2563eb") : "#f3f4f6",
        color: activeTab === id ? "#fff" : "#374151",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={sStyles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={sStyles.modal}>
        <div style={sStyles.header}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1a202c" }}>
              📋 Consultation History
            </div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{patientName || patientCard}</div>
          </div>
          <button style={sStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, padding: "10px 16px 0", borderBottom: "1px solid #e5e7eb", paddingBottom: 10, flexShrink: 0 }}>
          {tabBtn("history", `📋 GTP History (${history.length})`)}
          {tabBtn("bills",   `💜 Purchased Services (${bills.length} bills)`)}
        </div>

        <div style={sStyles.body}>
          {loading && <div style={{ padding: 24, color: "#888", fontSize: 13 }}>Loading…</div>}
          {error   && <div style={{ padding: 24, color: "#dc2626", fontSize: 13 }}>{error}</div>}

          {!loading && !error && activeTab === "history" && (
            <>
              {history.length === 0 && (
                <div style={{ padding: 24, color: "#aaa", fontSize: 13, textAlign: "center" }}>
                  No consultation history recorded yet.<br />
                  <span style={{ fontSize: 11 }}>History is recorded each time "Generate Treatment Plan" is used.</span>
                </div>
              )}
              {history.map((rev, i) => (
                <ReviewCard key={i} review={rev} idx={i} isLatest={i === 0} />
              ))}
            </>
          )}

          {!loading && !error && activeTab === "bills" && (
            <>
              {bills.length === 0 && (
                <div style={{ padding: 24, color: "#aaa", fontSize: 13, textAlign: "center" }}>
                  No billing records found for this patient.
                </div>
              )}
              {bills.map((bill, i) => (
                <BillCard key={bill._id || i} bill={bill} idx={i} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const sStyles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 3100, padding: 16,
  },
  modal: {
    background: "#fff", borderRadius: 14, width: "min(720px, 96vw)",
    maxHeight: "88vh", display: "flex", flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  },
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "16px 20px 12px", borderBottom: "1px solid #e8e8e8", flexShrink: 0,
  },
  closeBtn: {
    background: "none", border: "none", fontSize: 18, cursor: "pointer",
    color: "#888", padding: "0 0 0 12px",
  },
  body: { overflowY: "auto", flex: 1, padding: "14px 16px" },
  snapLabel: {
    fontSize: 11, fontWeight: 700, color: "#6b7280",
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 4,
  },
  grade: {
    fontSize: 11, background: "#f3f4f6", color: "#374151",
    borderRadius: 6, padding: "2px 8px",
  },
  meta: {
    fontSize: 11, background: "#f3f4f6", color: "#666",
    borderRadius: 4, padding: "1px 5px",
  },
};
